import type { BraceletPatternDto, PatternRow } from '@miyuki/types';

import { ValidationError } from '../errors';

import { MAX_COLUMNS, MIN_COLUMNS } from './pattern.constants';
import type { PatternEdit } from './pattern.entity';

export function assertWidth(columns: number): void {
  if (!Number.isInteger(columns) || columns < MIN_COLUMNS || columns > MAX_COLUMNS) {
    throw new ValidationError(`El ancho debe ser un entero entre ${MIN_COLUMNS} y ${MAX_COLUMNS}`, [
      { path: 'columns', message: `Recibido: ${columns}` },
    ]);
  }
}

/**
 * Cuántas mostacillas se perderían al estrechar el diseño a `columns`.
 *
 * No modifica nada: existe para que la interfaz pueda decir la cifra exacta
 * antes de pedir confirmación. Si la usuaria cancela, no se ejecutó ninguna
 * operación y no hay nada que revertir.
 */
export function countBeadsOutsideWidth(pattern: BraceletPatternDto, columns: number): number {
  assertWidth(columns);

  if (columns >= pattern.columns) return 0;

  return pattern.rows.reduce(
    (total, row) => total + row.slice(columns).filter((cell) => cell !== null).length,
    0,
  );
}

/**
 * Cambia el ancho del diseño. Ampliar rellena con celdas vacías a la derecha;
 * estrechar descarta lo que queda fuera — haber confirmado esa pérdida es
 * responsabilidad de quien llama, con `countBeadsOutsideWidth` en la mano.
 */
export function resizeColumns(pattern: BraceletPatternDto, columns: number): PatternEdit {
  assertWidth(columns);

  if (columns === pattern.columns) {
    return { pattern, undo: { kind: 'cells', cells: [] } };
  }

  const rows: readonly PatternRow[] = pattern.rows.map((row) =>
    columns < pattern.columns
      ? row.slice(0, columns)
      : [...row, ...Array.from({ length: columns - pattern.columns }, () => null)],
  );

  return {
    pattern: { ...pattern, columns, rows },
    // Cambiar el ancho recorta o rellena columnas, nunca filas (RF-66).
    undo: { kind: 'snapshot', pattern, progressDelta: 0 },
  };
}
